import { useMutation, useQueryClient } from "@tanstack/react-query";

import { LiveQueryKeys } from "@/components/Live/API/LiveQueries";

import { SATOSPortalNew } from "@/DataAdapter/Portal";

import { SavedLiveTagRequest } from "@/model/SATOSLive/Settings/SavedTagValue";
import { LiveSettingsRequest } from "@/model/SATOSLive/Settings/SettingsRequest";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { ProcedureQueryKeys } from "@/service/ProcedureQueries";

export const usePostLiveSettings = () => {
	const queryClient = useQueryClient();

	const { mutateAsync } = useMutation({
		mutationFn: (request: LiveSettingsRequest) => SATOSPortalNew.postLiveSettings(request),
			onMutate: (request: LiveSettingsRequest) => {
				queryClient.invalidateQueries({queryKey: [
					LiveQueryKeys.live,
					LiveQueryKeys.settings,
					ApplicationQueryKeys.user,
				]});

				const previousSettings = queryClient.getQueryData([
					LiveQueryKeys.live,
					LiveQueryKeys.settings,
					ApplicationQueryKeys.user,
				]);

				return previousSettings;
			},
			onError: (_, newSettings, previousSettings) => {
				queryClient.setQueryData(
					[LiveQueryKeys.live, LiveQueryKeys.settings, ApplicationQueryKeys.user],
					previousSettings,
				);
			},
			onSettled: () => {
				queryClient.invalidateQueries({queryKey: [
					LiveQueryKeys.live,
					LiveQueryKeys.settings,
					ApplicationQueryKeys.user,
				]});
				queryClient.invalidateQueries({queryKey: [LiveQueryKeys.omnia, LiveQueryKeys.timeseries]});
			},
		},
	);

	return { mutateAsync };
};

export const useSaveLiveTagValue = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: SavedLiveTagRequest) => SATOSPortalNew.saveLiveTagValue(request),
			onMutate: () => {
				queryClient.invalidateQueries({queryKey: [
					LiveQueryKeys.live,
					LiveQueryKeys.saveTagValue,
					ApplicationQueryKeys.user,
				]});

				const previousSettings = queryClient.getQueryData([
					LiveQueryKeys.live,
					LiveQueryKeys.saveTagValue,
					ApplicationQueryKeys.user,
				]);

				return previousSettings;
			},
			onError: (_, newSettings, previousSettings) => {
				queryClient.setQueryData(
					[
						LiveQueryKeys.live,
						LiveQueryKeys.saveTagValue,
						ApplicationQueryKeys.user,
					],
					previousSettings,
				);
			},
			onSettled: (variables) => {
				queryClient.invalidateQueries({queryKey: [
					LiveQueryKeys.live,
					LiveQueryKeys.saveTagValue,
					ApplicationQueryKeys.user,
				]});
				queryClient.invalidateQueries({queryKey: [
					ProcedureQueryKeys.procedureDetails,
					+variables?.ProcedureId,
					+variables?.SchemeId,
				]});
			},
		},
	);

	return mutation.mutateAsync;
};
